import React, { useRef, useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../layouts/Sidebar";
import UserMenu from "../components/UserMenu";
import Loader from "../components/Loader";
import authApi from "../utils/authApi";
import atlasApi from "../utils/atlasapi";
import globalContext from "../utils/globalContext";
import ScreeningOverlay, { DATASETS } from "../components/screening/ScreeningOverlay";

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import key from '../data/mapboxKey';

mapboxgl.accessToken = key;

const BASEMAPS = {
	outdoors:  { label: "Outdoors",  style: "mapbox://styles/mapbox/outdoors-v12" },
	satellite: { label: "Satellite", style: "mapbox://styles/mapbox/satellite-streets-v12" },
	light:     { label: "Light",     style: "mapbox://styles/mapbox/light-v11" }
};

/* ---- Main Page ---- */
export default function Screening() {
	const context = useContext(globalContext);
	const mapContainer = useRef(null);
	const mapRef = useRef(null);
	const [map, setMap] = useState(null);
	const [ready, setReady] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [user, setUser] = useState({});
	const [basemap, setBasemap] = useState("outdoors");

	// Page body class for any global hooks
	useEffect(() => {
		document.body.classList.add("screening-page");
		document.title = (process.env.REACT_APP_DOC_TITLE || "Allod Energy Atlas") + " · Screening";
		return () => document.body.classList.remove("screening-page");
	}, []);

	// Fetch API token, then load user (getUser calls the API internally)
	useEffect(() => {
		let alive = true;
		atlasApi.getToken()
			.catch(() => null)
			.then(() => authApi.getUser())
			.then((r) => (r && r.json ? r.json() : r))
			.catch(() => ({}))
			.then((u) => { if (alive) setUser(u || {}); });
		return () => { alive = false; };
	}, []);

	// Initialize the map (once)
	useEffect(() => {
		if (mapRef.current || !mapContainer.current) return;
		const m = new mapboxgl.Map({
			container: mapContainer.current,
			style: BASEMAPS[basemap].style,
			center: DATASETS.all.center,
			zoom: DATASETS.all.zoom,
			attributionControl: false
		});
		m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "bottom-right");
		m.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
		m.dragRotate.disable();

		m.on("load", () => { setReady(true); });

		mapRef.current = m;
		setMap(m);
		return () => { m.remove(); mapRef.current = null; setMap(null); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Basemap switch — the overlay re-adds its layers when `basemap` changes
	useEffect(() => {
		if (!mapRef.current || !ready) return;
		mapRef.current.setStyle(BASEMAPS[basemap].style);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [basemap]);

	return (
		<React.Fragment>
			<div className="header">
				<div className="menu">
					<i className="ri-menu-2-fill"></i>
				</div>
				<div className="middle">
					<Link to="/" className="header-logo">Energy Atlas</Link>
					<div className="header-version">{context && context.APP_VERSION}</div>
				</div>
				<div className="logo">
					<img className="img-fluid d-none d-md-block brand-logo" src="/images/allod_atlas_logo.png" alt="Allod" />
				</div>
				<div className="options">
					{user && user.userId && <UserMenu user={user} onUpdateUser={setUser} />}
				</div>
			</div>
			<div className="body dashboard2">
				<Sidebar />
				<div className="main projects screening-main map-wrapper">
					<div ref={mapContainer} className="screening-map" />

					<div className="basemap-panel">
						{Object.keys(BASEMAPS).map((k) => (
							<button
								key={k}
								className={"basemap-btn " + (basemap === k ? "on" : "")}
								onClick={() => setBasemap(k)}>
								{BASEMAPS[k].label}
							</button>
						))}
					</div>

					<ScreeningOverlay
						map={map}
						mapReady={ready}
						styleKey={basemap}
						onLoadingChange={setLoaded}
					/>
				</div>
			</div>

			<Loader loaded={loaded} message="Loading screening data..." />
		</React.Fragment>
	);
}
